import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { CommunityActivityActionPlansSummary } from 'types/recommendations';

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 0 24px;
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-flow: row;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding-top: 20px;
`;

const ColumnTitle = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
`;

const ColumnLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 10px;
  padding-bottom: 30px;
`;

const ProgressPlans = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
`;

const ProgressTrack = styled.div`
  display: flex;
  flex-flow: row;
  width: 100%;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--line-gray);
`;

const ProgressSegment = styled.div`
  height: 100%;

  &.completed {
    background-image: linear-gradient(
      to bottom,
      var(--communication-channel-call-gradient-start),
      var(--positive-green)
    );
  }
  &.in-progress {
    background-image: linear-gradient(to bottom, var(--communication-channel-email-gradient-start), var(--active-blue));
  }
  &.not-started {
    background-image: linear-gradient(to bottom, var(--slate-gray-light), var(--slate-gray));
  }
`;

const Legend = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  margin-top: 12px;
`;

const LegendRow = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

const LegendDot = styled.span`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;

  &.completed {
    background-image: linear-gradient(
      to bottom,
      var(--communication-channel-call-gradient-start),
      var(--positive-green)
    );
  }
  &.in-progress {
    background-image: linear-gradient(to bottom, var(--communication-channel-email-gradient-start), var(--active-blue));
  }
  &.not-started {
    background-image: linear-gradient(to bottom, var(--slate-gray-light), var(--slate-gray));
  }
`;

const OverdueRow = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 10px;
  padding-top: 10px;
  padding-bottom: 10px;
  border-top: solid 1px var(--line-gray);
  border-bottom: solid 1px var(--line-gray);
`;

const OverdueAccent = styled.div`
  width: 4px;
  align-self: stretch;
  border-radius: 4px;
  background: linear-gradient(to bottom, var(--amber-light), var(--inactive-orange));
`;

const OverdueText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
`;

type Props = {
  actionPlans?: CommunityActivityActionPlansSummary;
  actionPlansPath: string;
};

const ActionPlanStatusColumn = ({ actionPlans, actionPlansPath }: Props) => {
  const navigate = useNavigate();

  const completedCount = actionPlans?.completed_count ?? 0;
  const inProgressCount = actionPlans?.in_progress_count ?? 0;
  const notStartedCount = actionPlans?.not_started_count ?? 0;
  const totalCount = actionPlans?.total_count ?? 0;
  const overdueCount = actionPlans?.overdue_count ?? 0;
  const unassignedCount = actionPlans?.unassigned_count ?? 0;

  const goTo = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <Column data-test-id="activity-summary-action-plans-column">
      <ColumnHeader>
        <ColumnTitle>Action Plan Status</ColumnTitle>
        <ColumnLink
          data-test-id="activity-summary-action-plans-link"
          href={actionPlansPath}
          onClick={goTo(actionPlansPath)}
        >
          All Plans ({totalCount}) <img src="/icons/chevron-right-blue.svg" width="16" height="16" alt="" />
        </ColumnLink>
      </ColumnHeader>
      <Content>
        <ProgressPlans>
          <ProgressTrack>
            {totalCount > 0 && (
              <>
                <ProgressSegment className="completed" style={{ width: `${(completedCount / totalCount) * 100}%` }} />
                <ProgressSegment
                  className="in-progress"
                  style={{ width: `${(inProgressCount / totalCount) * 100}%` }}
                />
                <ProgressSegment
                  className="not-started"
                  style={{ width: `${(notStartedCount / totalCount) * 100}%` }}
                />
              </>
            )}
          </ProgressTrack>
          <Legend>
            <LegendRow>
              <LegendDot className="completed" />
              {completedCount} Completed
            </LegendRow>
            <LegendRow>
              <LegendDot className="in-progress" />
              {inProgressCount} In Progress
            </LegendRow>
            <LegendRow>
              <LegendDot className="not-started" />
              {notStartedCount} Not Started
            </LegendRow>
          </Legend>
        </ProgressPlans>
        <OverdueRow>
          <OverdueAccent />
          <OverdueText>
            {overdueCount} Overdue · {unassignedCount} Unassigned
          </OverdueText>
        </OverdueRow>
      </Content>
    </Column>
  );
};

export default ActionPlanStatusColumn;
